import { useState, useEffect } from "react"
import { LoadingSpinner } from "@/components/ui/LoadingSpinner"
import { useParams, useNavigate } from "react-router-dom"
import { trackEvent } from "@/lib/mixpanel"
import { ArrowLeft, Download, Edit, Calendar, MapPin, Users, FileText, Plus, Search, DollarSign, Clock, Building2, Tag, ImageIcon } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { useEvent } from "@/hooks/useEvents"
import { useBrands } from "@/hooks/useBrands"
import { useCampaigns } from "@/hooks/useCampaigns"
import { formatCurrency } from "@/lib/utils/currency"
import { format } from "date-fns"
import { EventGallery } from "@/components/events/EventGallery"
import { EditEventModal } from "@/components/events/EditEventModal"

import { EventVendorsTab } from '@/components/events/EventVendorsTab'
import { EventGuestsTab } from '@/components/events/EventGuestsTab'
import { EventDeliverablesTab } from '@/components/events/EventDeliverablesTab'

export default function EventDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState("vendors")

  const { data: event, isLoading: eventLoading } = useEvent(id!)
  const { data: brands } = useBrands()
  const { data: campaigns } = useCampaigns()

  useEffect(() => {
    if (event) {
      trackEvent('Event Viewed', {
        event_id: event.id,
        event_name: event.name,
        event_status: event.status,
      });
    }
  }, [event]);

  if (eventLoading) {
    return <LoadingSpinner size="fullscreen" />
  }

  if (!event) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-900">Event not found</h2>
        <Button onClick={() => navigate("/events")} className="mt-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Events
        </Button>
      </div>
    )
  }

  const getBrandName = (brandId: string) => {
    return brands?.find(b => b.id === brandId)?.name || "N/A"
  }

  const getCampaignName = (campaignId: string) => {
    return campaigns?.find(c => c.id === campaignId)?.name || "N/A"
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case "upcoming": return "bg-blue-100 text-blue-800"
      case "completed": return "bg-green-100 text-green-800"
      case "planning": return "bg-yellow-100 text-yellow-800"
      case "cancelled": return "bg-red-100 text-red-800"
      default: return "bg-gray-100 text-gray-800"
    }
  }

  const formatDateTime = (dateString: string, timeString?: string) => {
    const date = format(new Date(dateString), "MMMM dd, yyyy")
    return timeString ? `${date} at ${timeString}` : date
  }

  const remaining = (event.total_budget || 0) - (event.total_spent || 0)

  return (
    <div className="min-h-screen bg-gradient-to-br from-white to-purple-50">
      <div className="container mx-auto p-6">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate("/events")}
              className="text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Events
            </Button>
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-3xl font-bold text-gray-900">{event.name}</h1>
                <Badge className={getStatusColor(event.status) + ' text-xs'}>
                  {event.status}
                </Badge>
              </div>
              <p className="text-gray-600 flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                {formatDateTime(event.event_date, event.event_time)}
              </p>
            </div>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" className="flex items-center gap-2">
              <Download className="h-4 w-4" />
              Export Summary
            </Button>
            <EditEventModal event={event} />
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="bg-white shadow-lg border-0">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Total Budget</CardTitle>
              <div className="h-8 w-8 rounded-lg bg-green-100 flex items-center justify-center">
                <DollarSign className="h-4 w-4 text-green-600" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gray-900">{formatCurrency(event.total_budget || 0, "IN")}</div>
            </CardContent>
          </Card>

          <Card className="bg-white shadow-lg border-0">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Total Spent</CardTitle>
              <div className="h-8 w-8 rounded-lg bg-red-100 flex items-center justify-center">
                <DollarSign className="h-4 w-4 text-red-600" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gray-900">{formatCurrency(event.total_spent || 0, "IN")}</div>
            </CardContent>
          </Card>

          <Card className="bg-white shadow-lg border-0">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Remaining</CardTitle>
              <div className="h-8 w-8 rounded-lg bg-blue-100 flex items-center justify-center">
                <DollarSign className="h-4 w-4 text-blue-600" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gray-900">{formatCurrency(remaining, "IN")}</div>
            </CardContent>
          </Card>

          <Card className="bg-white shadow-lg border-0">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Event Type</CardTitle>
              <div className="h-8 w-8 rounded-lg bg-purple-100 flex items-center justify-center">
                <Tag className="h-4 w-4 text-purple-600" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-xl font-bold text-gray-900">{event.event_type}</div>
            </CardContent>
          </Card>
        </div>

        {/* Event Information Section */}
        <Card className="mb-8 bg-white shadow-lg border-0">
          <CardHeader>
            <CardTitle className="text-xl font-bold text-gray-900">Event Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Left Column - Details */}
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-900 mb-4">Details</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Building2 className="h-4 w-4 text-gray-500" />
                    <div>
                      <span className="text-sm font-medium text-gray-600">Brand: </span>
                      <span className="text-gray-900">{getBrandName(event.brand_id!)}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Tag className="h-4 w-4 text-gray-500" />
                    <div>
                      <span className="text-sm font-medium text-gray-600">Campaign: </span>
                      <span className="text-gray-900">{getCampaignName(event.campaign_id!)}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <MapPin className="h-4 w-4 text-gray-500" />
                    <div>
                      <span className="text-sm font-medium text-gray-600">Venue: </span>
                      <span className="text-gray-900">{event.venue_address || "TBD"}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Clock className="h-4 w-4 text-gray-500" />
                    <div>
                      <span className="text-sm font-medium text-gray-600">Date & Time: </span>
                      <span className="text-gray-900">{formatDateTime(event.event_date, event.event_time)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column - Description & Documents */}
              <div className="space-y-6">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Description</h3>
                  <p className="text-gray-700 leading-relaxed">
                    {event.description || "No description provided."}
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Documents</h3>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-blue-600 hover:text-blue-800 cursor-pointer">
                      <FileText className="h-4 w-4" />
                      <span className="text-sm">Floor Plan.pdf</span>
                    </div>
                    <div className="flex items-center gap-2 text-blue-600 hover:text-blue-800 cursor-pointer">
                      <FileText className="h-4 w-4" />
                      <span className="text-sm">Event Brief.docx</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tab Navigation */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-5 mb-6">
            <TabsTrigger value="vendors" className="flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              Vendors
            </TabsTrigger>
            <TabsTrigger value="guests" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Guest List
            </TabsTrigger>
            <TabsTrigger value="deliverables" className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Deliverables
            </TabsTrigger>
            <TabsTrigger value="budget" className="flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              Budget
            </TabsTrigger>
            <TabsTrigger value="gallery" className="flex items-center gap-2">
              <ImageIcon className="h-4 w-4" />
              Gallery
            </TabsTrigger>
          </TabsList>

          <TabsContent value="vendors">
            <EventVendorsTab eventId={id!} />
          </TabsContent>

          <TabsContent value="guests">
            <EventGuestsTab eventId={id!} eventName={event.name} />
          </TabsContent>

          <TabsContent value="deliverables">
            <EventDeliverablesTab eventId={id!} />
          </TabsContent>

          {/* Budget Tab Content */}
          <TabsContent value="budget">
            <Card className="bg-white shadow-lg border-0">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle className="text-xl font-bold text-gray-900">Budget Breakdown</CardTitle>
                    <p className="text-gray-600 mt-1">Detailed view of event expenses</p>
                  </div>
                  <Button className="bg-purple-600 hover:bg-purple-700 text-white">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Expense
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="mb-6">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="font-medium text-gray-700">Budget Utilization</span>
                    <span className="font-bold text-gray-900">
                      {formatCurrency(event.total_spent || 0, "IN")} / {formatCurrency(event.total_budget || 0, "IN")}
                    </span>
                  </div>
                </div>

                <div className="space-y-6 mt-8">
                  <h3 className="text-lg font-semibold text-gray-900">Expense Categories</h3>
                  <div className="space-y-4">
                    <p className="text-gray-500 italic">Budget tracking coming soon.</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Gallery Tab Content */}
          <TabsContent value="gallery">
            <EventGallery eventId={id!} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  )
}
